import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Ticket, Calendar, MapPin, CheckCircle, Clock, XCircle, Eye } from 'lucide-react';
import axios from 'axios';
import { useTheme } from '../context/ThemeContext';
import API_URL from '../api';

function MyTickets() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelMsg, setCancelMsg] = useState('');
  const navigate = useNavigate();
  const { darkMode } = useTheme();
  const user = JSON.parse(localStorage.getItem('user'));

  const bg = darkMode ? '#0f0f1a' : '#f0f2f5';
  const card = darkMode ? '#1a1a2e' : '#fff';
  const border = darkMode ? 'rgba(255,255,255,0.07)' : '#f0f0f0';
  const text = darkMode ? '#e0e0e0' : '#333';
  const subtext = darkMode ? '#888' : '#666';

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) { navigate('/login'); return; }
    fetchTickets();
  }, [navigate]);

  const fetchTickets = () => {
    const token = localStorage.getItem('token');
    axios.get(`${API_URL}/api/auth/my-tickets`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => { setTickets(res.data); setLoading(false); })
      .catch(() => navigate('/login'));
  };

  const handleCancel = async (ticketId) => {
    if (!window.confirm('Cancel this registration?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/registrations/${ticketId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCancelMsg({ type: 'success', text: 'Registration cancelled successfully!' });
      fetchTickets();
    } catch (err) {
      setCancelMsg({ type: 'error', text: err.response?.data?.error || 'Cancel failed!' });
    }
  };

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh', background: bg }}>
      <div>
        <div style={{ width: '40px', height: '40px', border: '3px solid #e94560', borderTop: '3px solid transparent', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 15px' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        <p style={{ color: subtext }}>Loading your tickets...</p>
      </div>
    </div>
  );

  return (
    <div style={{ background: bg, minHeight: '100vh', padding: '30px 20px' }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } } @keyframes fadeIn { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }`}</style>
      <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
        <div style={{ marginBottom: '30px' }}>
          <h1 style={{ color: text, fontSize: '28px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Ticket color="#e94560" size={28} /> My Tickets
          </h1>
          <p style={{ color: subtext, marginTop: '5px' }}>Welcome back, <strong style={{ color: '#e94560' }}>{user?.name}</strong>!</p>
        </div>

        {cancelMsg && (
          <div style={{ padding: '14px 18px', borderRadius: '12px', marginBottom: '20px', background: cancelMsg.type === 'success' ? (darkMode ? 'rgba(76,175,80,0.15)' : '#e8f5e9') : (darkMode ? 'rgba(233,69,96,0.15)' : '#ffebee'), color: cancelMsg.type === 'success' ? '#4caf50' : '#e94560', border: `1px solid ${cancelMsg.type === 'success' ? 'rgba(76,175,80,0.3)' : 'rgba(233,69,96,0.3)'}`, animation: 'fadeIn 0.3s ease' }}>
            {cancelMsg.type === 'success' ? '✅' : '❌'} {cancelMsg.text}
          </div>
        )}

        {tickets.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '70px', background: card, borderRadius: '20px', border: `1px solid ${border}` }}>
            <Ticket size={50} color="#e94560" style={{ marginBottom: '15px', opacity: 0.5 }} />
            <h3 style={{ color: subtext, fontWeight: '400', fontSize: '18px' }}>No tickets yet</h3>
            <p style={{ color: subtext, marginTop: '8px', fontSize: '14px' }}>Register for an event to see your tickets here!</p>
            <button onClick={() => navigate('/')} style={{ marginTop: '20px', background: 'linear-gradient(135deg, #e94560, #c62a47)', color: '#fff', padding: '12px 30px', borderRadius: '10px' }}>Browse Events</button>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '25px' }}>
            {tickets.map((ticket, i) => (
              <div key={ticket._id} style={{ background: card, borderRadius: '20px', overflow: 'hidden', border: `1px solid ${border}`, boxShadow: darkMode ? '0 4px 20px rgba(0,0,0,0.3)' : '0 2px 15px rgba(0,0,0,0.07)', animation: `fadeIn 0.4s ease ${i * 0.1}s both` }}>
                <div style={{ background: 'linear-gradient(135deg, #1a1a2e, #0f3460)', padding: '20px', position: 'relative' }}>
                  <div style={{ position: 'absolute', top: 0, right: 0, width: '80px', height: '80px', background: 'rgba(233,69,96,0.1)', borderRadius: '0 0 0 100%' }} />
                  <span style={{ background: 'rgba(233,69,96,0.2)', color: '#e94560', padding: '4px 12px', borderRadius: '20px', fontSize: '11px', fontWeight: '600', display: 'inline-block', marginBottom: '8px' }}>🎟️ TICKET</span>
                  <h2 style={{ color: '#fff', fontSize: '17px', fontWeight: '700' }}>{ticket.eventId?.title || 'Event'}</h2>
                </div>
                <div style={{ padding: '18px', textAlign: 'center', borderBottom: `1px solid ${border}` }}>
                  <div style={{ background: '#fff', borderRadius: '12px', padding: '8px', display: 'inline-block', boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}>
                    <img src={ticket.qrCode} alt="QR" style={{ width: '120px', height: '120px', display: 'block' }} />
                  </div>
                </div>
                <div style={{ padding: '18px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '7px', marginBottom: '15px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: subtext }}><Calendar size={13} color="#e94560" />{new Date(ticket.eventId?.date).toDateString()}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: subtext }}><MapPin size={13} color="#e94560" />{ticket.eventId?.venue}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
                      {ticket.checkedIn
                        ? <><CheckCircle size={13} color="#4caf50" /><span style={{ color: '#4caf50', fontWeight: '600' }}>Checked In</span></>
                        : <><Clock size={13} color="#ff9800" /><span style={{ color: '#ff9800', fontWeight: '600' }}>Not checked in yet</span></>}
                    </div>
                  </div>
                  <button onClick={() => navigate(`/ticket/${ticket._id}`)} style={{ background: darkMode ? 'rgba(255,255,255,0.08)' : '#f5f5f5', color: text, width: '100%', padding: '11px', borderRadius: '10px', marginBottom: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '7px', fontSize: '14px' }}>
                    <Eye size={15} /> View Full Ticket
                  </button>
                  {!ticket.checkedIn && ticket.eventId?.allowCancellation && (
                    <button onClick={() => handleCancel(ticket._id)} style={{ background: darkMode ? 'rgba(233,69,96,0.12)' : '#ffebee', color: '#e94560', width: '100%', padding: '11px', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '7px', fontSize: '14px', border: '1px solid rgba(233,69,96,0.2)' }}>
                      <XCircle size={15} /> Cancel Registration
                    </button>
                  )}
                  {!ticket.eventId?.allowCancellation && (
                    <p style={{ textAlign: 'center', color: subtext, fontSize: '12px', marginTop: '8px' }}>🔒 Cancellation not allowed</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default MyTickets;